import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FileText, Download, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { diyProcesses } from '@/data/diyProcesses';
import { useEffect, useState } from 'react';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto lg:max-w-5xl transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

// Animation component for sections appearing
const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </div>
  );
};

// Animation component for grid items with staggered delay
const AnimatedGridItem = ({ children, index }: { children: React.ReactNode; index: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 1000 + (index * 100));
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div
      className={`transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </div>
  );
};

const DIYProcesses = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Stand on Your Own two Feet
            </h1>
            
            <AnimatedSection delay={200}>
              <div className="bg-gold/10 border border-gold/20 rounded-lg p-6 max-w-4xl mx-auto mb-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                  <div className="text-left">
                    <h3 className="font-semibold text-gold mb-2">Important Disclaimer</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      <strong>USE AND EDIT DOCUMENTS AT YOUR OWN RISK. THEY ARE PROVIDED FOR FREE—Our time, however, is not—please value it and approach with respect.</strong>
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      It is not the paper or the ink that give these tools power—it is your understanding of whats going on, combined with your ability to express this understanding in a structured manner—that make these tools perform a desired outcome.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      If you choose to walk this path, you must see it through. We are not weak in mind or heart. We are abolitionists. We are anarchists. We demand freedom at call costs, without violating the rights of others. We do not retreat. We do not surrender. We do not stop until the mission is complete. In others words, If you're going to start at all, you may as well make yourself at home.
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={400}>
              <blockquote className="italic text-foreground/90 max-w-3xl mx-auto mb-6">
                "Those who hope to be both ignorant and free are hoping for something that never has been— and never will be." <span className="not-italic font-medium">— Thomas Jefferson</span>
              </blockquote>
            </AnimatedSection>
            
            <AnimatedParagraph delay={600}>
              Success comes with the hunger for knowledge, the will to understand, and the fortitude to act. It doesn't come from just following what others have done. These tools are powerful, but YOU make them work. These processes have worked for many of us, but as you learn and grow, you must inevitably develop your own solutions based on your own understanding, according to the specific goals you have. And when you do— pay it forward. Each one reach one—this is how we grow.
            </AnimatedParagraph>
          </div>

          <AnimatedSection delay={800}>
            <div className="text-center mb-8">
              <p className="font-rockwell font-bold text-muted-foreground text-lg">
                We recommend going in the order that the processes appear for comprehension.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {diyProcesses.map((process, index) => (
              <AnimatedGridItem key={index} index={index}>
                <Link to={`/diy-processes/${process.id}`}>
                  <div className="group bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-[460px]">
                    <div className="flex items-start mb-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <FileText className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                    
                    <h3 className="font-rockwell text-xl font-semibold text-foreground mb-3">
                      {process.title}
                    </h3>
                    
                    <p className="text-muted-foreground mb-4 leading-relaxed flex-grow">
                      {process.description}
                    </p>
                    
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>Estimated time: {process.timeEstimate}</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Download className="h-4 w-4 mr-2" />
                        <span>{process.documents} templates included</span>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full group-hover:bg-primary/90 transition-colors mt-auto"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Start Process
                    </Button>
                  </div>
                </Link>
              </AnimatedGridItem>
            ))}
          </div>

          <AnimatedSection delay={1200}>
            <div className="mt-16 text-center">
              <div className="bg-muted/50 border border-gold/10 rounded-lg p-8">
                <h3 className="font-rockwell text-2xl font-semibold text-foreground mb-4">
                  Complete Independence
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                  These processes are designed for complete self-sufficiency. Each includes detailed instructions, 
                  template documents, legal references, and step-by-step guidance. No external assistance required.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default DIYProcesses;